import moment from "moment";
import { getMinBudget } from "../../../../../utils/Helpers";
import {
  clientFollowedRecommendations,
  getDefaultBudget
} from "../../../../Campaign/Campaign__Budget-Calculator-utils";
import { DEFAULT_MINUTES_LEFT_TO_START } from "../../../Previews/constants";
import { COUNTRIES_WITHOUT_TAXES } from "../../invoice-address-modal/utils";
import { DISCOUNT_LIMIT, PICTURE_PRICE, SALES_TAXES_RATE } from "../../utils/constants";
import {
  // analysis feedback video review
  ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_DESCRIPTION,
  ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_ID,
  ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_NAME,
  // analysis optimization video review
  ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_DESCRIPTION,
  ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_ID,
  ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_NAME,
  // campaign item
  CAMPAIGN_ITEM_ID,
  CAMPAIGN_ITEM_NAME,
  // shutterstock image
  SHUTTERSTOCK_POSITION_DESCRIPTION,
  SHUTTERSTOCK_POSITION_ID,
  SHUTTERSTOCK_POSITION_NAME,
  SHUTTERSTOCK_POSITION_PRICE_PER_UNIT,
  // shutterstock video
  SHUTTERSTOCK_POSITION_DESCRIPTION_VIDEO,
  SHUTTERSTOCK_POSITION_ID_VIDEO,
  SHUTTERSTOCK_POSITION_NAME_VIDEO,
  SHUTTERSTOCK_POSITION_PRICE_PER_UNIT_VIDEO,
  // spotify human voice
  SPOTIFY_HUMAN_VOICE_POSITION_DESCRIPTION,
  SPOTIFY_HUMAN_VOICE_POSITION_ID,
  SPOTIFY_HUMAN_VOICE_POSITION_NAME
} from "../BookingOverview.constants";

function roundNum(number) {
  return +(Math.round(number + "e+2") + "e-2");
}

export function formatNumber(amount = 0) {
  let value = parseFloat(amount);

  if (!value || isNaN(value) || Array.isArray(value)) {
    return roundNum(0);
  }

  return roundNum(value);
}

export function calculateDiscount({ amount, percent }) {
  if (!percent) {
    return 0;
  }

  const discount = percent / 100;
  if (discount > DISCOUNT_LIMIT) {
    return 0;
  }

  return formatNumber(amount * discount);
}

export function getDiscountFromSource({ organization, coupon, items, isAgency }) {
  const couponItem = coupon?.item || CAMPAIGN_ITEM_ID;
  const orgaDiscount = parseInt(organization?.discount || 0, 10);
  const couponDiscount = parseInt(coupon?.discount || 0, 10);
  const discountTypeCoupon = coupon?.type;

  const foundItem = items?.find((item) => item.id === couponItem);
  const { value: amount = 0, name: discountItemName } = foundItem || {};

  if (!orgaDiscount && !couponDiscount) {
    return null;
  }

  if ((orgaDiscount >= couponDiscount && discountTypeCoupon === "percent") || isAgency) {
    return {
      deduction: calculateDiscount({ amount, percent: orgaDiscount }),
      discount: orgaDiscount,
      type: "organization",
      discountType: "percent",
      discountItemName: "Agency"
    };
  }

  let deduction = calculateDiscount({ amount, percent: couponDiscount });
  if (discountTypeCoupon === "absolute") {
    deduction = couponDiscount;
  }

  return {
    deduction,
    discount: couponDiscount,
    type: "coupon",
    discountType: discountTypeCoupon,
    discountItemName
  };
}

export function getPrepaidCredit({ prepaidBalance, subtotal }) {
  const credit = parseFloat(prepaidBalance);

  if (!credit) {
    return 0;
  }

  if (credit < subtotal) {
    return credit;
  }

  return subtotal;
}

export function getSubtotal({ amount, discount = 0 }) {
  const amountNumber = parseFloat(amount);
  const discountNumber = parseFloat(discount);
  return formatNumber(amountNumber - discountNumber);
}

export function getTaxes({ subtotal, countryCode, credit = 0 }) {
  const subtotalNumber = parseFloat(subtotal);
  // When a customer uses the credit/prepaid money we do not apply the 19% taxes, cause the customer already paid that taxes when selecting the prepaid package.
  if (credit >= subtotal) {
    return 0;
  }

  if (COUNTRIES_WITHOUT_TAXES.includes(countryCode)) {
    return 0;
  }

  return formatNumber((subtotalNumber - credit) * SALES_TAXES_RATE);
}

export function getTotal({ subtotal, taxes = 0, credit = 0 }) {
  const subtotalNumber = parseFloat(subtotal);
  const taxesNumber = parseFloat(taxes);
  const creditNumber = parseFloat(credit);
  if (credit >= subtotal) {
    return 0;
  }

  return formatNumber(subtotalNumber + taxesNumber - creditNumber);
}

export function getExtraLocationCosts(extraBudgetLocations = []) {
  return extraBudgetLocations.reduce((acc, location) => {
    return location.price ? acc + location.price : acc;
  }, 0);
}

export const extractCreativePictures = (posts) => {
  if (!posts) {
    return [];
  }

  if (typeof posts !== "object" || Array.isArray(posts)) {
    return [];
  }

  const allCreatives = new Set();
  Object.values(posts)
    .flat()
    .forEach(({ creatives }) => {
      if (!creatives) {
        return;
      }

      // First one represent a selected imaged on the booking, not licensed or saved yet
      // Second one represent a licensed image that was uploaded to cloudinary and has the cloudinary url
      const shutterstockImages = [
        ...creatives.filter((c) => c.includes("image.shutterstock.com")),
        ...creatives.filter((c) => c.includes("organizations-stock-images"))
      ];

      shutterstockImages.forEach((si) => {
        allCreatives.add(si);
      });
    });

  return Array.from(allCreatives);
};

export const buildCampaignInvoiceAddOns = ({
  allCreativeImages = [],
  allCreativeVideos = [],
  addOns
}) => {
  const campaignInvoiceAddOns = [];
  const createdDate = new Date().toISOString();
  const { feedbackAnalysis, optimizeAnalysis, spotifyHumanVoice } = addOns;

  if (allCreativeImages?.length !== 0) {
    campaignInvoiceAddOns.push({
      id: SHUTTERSTOCK_POSITION_ID,
      name: SHUTTERSTOCK_POSITION_NAME,
      description: SHUTTERSTOCK_POSITION_DESCRIPTION,
      price: SHUTTERSTOCK_POSITION_PRICE_PER_UNIT,
      quantity: allCreativeImages.length,
      createdDate
    });
  }

  if (allCreativeVideos?.length !== 0) {
    campaignInvoiceAddOns.push({
      id: SHUTTERSTOCK_POSITION_ID_VIDEO,
      name: SHUTTERSTOCK_POSITION_NAME_VIDEO,
      description: SHUTTERSTOCK_POSITION_DESCRIPTION_VIDEO,
      price: SHUTTERSTOCK_POSITION_PRICE_PER_UNIT_VIDEO,
      quantity: allCreativeVideos.length,
      createdDate
    });
  }

  if (feedbackAnalysis) {
    campaignInvoiceAddOns.push({
      id: ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_ID,
      name: ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_NAME,
      description: ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_DESCRIPTION,
      price: feedbackAnalysis,
      quantity: 1,
      createdDate
    });
  }

  if (optimizeAnalysis) {
    campaignInvoiceAddOns.push({
      id: ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_ID,
      name: ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_NAME,
      description: ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_DESCRIPTION,
      price: optimizeAnalysis,
      quantity: 1,
      createdDate
    });
  }

  if (spotifyHumanVoice) {
    campaignInvoiceAddOns.push({
      id: SPOTIFY_HUMAN_VOICE_POSITION_ID,
      name: SPOTIFY_HUMAN_VOICE_POSITION_NAME,
      description: SPOTIFY_HUMAN_VOICE_POSITION_DESCRIPTION,
      price: spotifyHumanVoice,
      quantity: 1,
      createdDate
    });
  }

  return campaignInvoiceAddOns;
};

export const getCombinedBudget = ({
  allCreativeImages,
  extraBudgetLocations,
  lifeTimeBudget,
  addOns
}) => {
  const extraLocationsCost = getExtraLocationCosts(extraBudgetLocations);
  const campaignBudget = lifeTimeBudget + extraLocationsCost;

  // optimizeAnalysis, feedbackAnalysis, spotifyHumanVoice only
  // these addons do not include shutterstock images
  const activeAddOns = Object.values(addOns).filter((value) => value !== null);
  const addOnsTotal = activeAddOns.length * 49;

  const shutterstockImagesPrice = allCreativeImages.length * PICTURE_PRICE;

  const combinedBudget =
    lifeTimeBudget + extraLocationsCost + shutterstockImagesPrice + addOnsTotal;

  return { campaignBudget, shutterstockImagesPrice, combinedBudget };
};

export const getDiscountItems = ({
  campaignBudget,
  shutterstockImagesPrice,
  shutterstockVideosPrice,
  addOns
}) => {
  const { feedbackAnalysis, optimizeAnalysis, spotifyHumanVoice } = addOns;
  return [
    { id: CAMPAIGN_ITEM_ID, name: CAMPAIGN_ITEM_NAME, value: campaignBudget || 0 },
    {
      id: SHUTTERSTOCK_POSITION_ID,
      name: SHUTTERSTOCK_POSITION_NAME,
      value: shutterstockImagesPrice || 0
    },
    {
      id: SHUTTERSTOCK_POSITION_ID_VIDEO,
      name: SHUTTERSTOCK_POSITION_NAME_VIDEO,
      value: shutterstockVideosPrice || 0
    },
    {
      id: ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_ID,
      name: ANALYSIS_FEEDBACK_VIDEO_REVIEW_POSITION_NAME,
      value: feedbackAnalysis || 0
    },
    {
      id: ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_ID,
      name: ANALYSIS_OPTIMIZATION_VIDEO_REVIEW_POSITION_NAME,
      value: optimizeAnalysis || 0
    },
    {
      id: SPOTIFY_HUMAN_VOICE_POSITION_ID,
      name: SPOTIFY_HUMAN_VOICE_POSITION_NAME,
      value: spotifyHumanVoice || 0
    }
  ];
};

export const assembleCampaignData = ({
  user,
  channels,
  predictions,
  campaignDuration,
  recommendedChannels,
  selectedChannels,
  lifeTimeBudget,
  allCreativeImages,
  addOns,
  activeDate,
  bookingStyle,
  campaignGoal,
  campaignManager,
  optimizeAnalysis,
  feedbackAnalysis,
  spotifyHumanVoice,
  campaignName,
  canUseWebsitePictures,
  companyName,
  companyId,
  createdDate,
  deployed,
  extraBudgetLocations,
  hasNewPreviewFlow,
  invoicing,
  isCreatedFromDraft,
  isRemoteJob,
  jobCategory,
  jobCategoryId,
  jobPostingUrl,
  jobTitle,
  combinedBudget,
  campaignBudget,
  locations,
  minClicks,
  orgaName,
  organizationId,
  packageId,
  paymentStatus,
  pictures,
  source,
  startDate,
  status,
  willUploadPicturesLater,
  locale,
  jobId,
  couponId,
  poNumber,
  selfServiceBookingStyle
}) => {
  let withAdPreview = true;

  const budgetDefault = getDefaultBudget({
    predictions,
    minBudget: getMinBudget(campaignDuration)
  });

  const { followedBudgetRecommendation, followedChannelRecommendation } =
    clientFollowedRecommendations({
      recommendedChannels,
      selectedChannels,
      budget: lifeTimeBudget
    });

  const recommendedChannelNames = recommendedChannels.map(
    ({ channel_short_name }) => channel_short_name
  );

  const budgetRecommendation = {
    channels: recommendedChannelNames,
    budget: budgetDefault
  };

  const campaignInvoiceAddOns = buildCampaignInvoiceAddOns({ allCreativeImages, addOns });

  const campaignData = {
    activeDate,
    bookingStyle,
    campaignDuration,
    campaignGoal,
    campaignManager,
    optimizeAnalysis,
    feedbackAnalysis,
    spotifyHumanVoice,
    campaignInvoiceAddOns,
    campaignName,
    canUseWebsitePictures,
    channels,
    companyName,
    companyId,
    createdDate,
    deployed,
    extraBudgetLocations,
    hasNewPreviewFlow,
    invoicing,
    isCreatedFromDraft,
    isRemoteJob,
    jobCategory,
    jobCategoryId,
    jobPostingUrl,
    jobTitle,
    combinedBudget,
    lifeTimeBudget: campaignBudget,
    locations,
    minClicks,
    orgaName,
    organizationId,
    packageId,
    paymentStatus,
    pictures,
    selectedChannels,
    source,
    startDate,
    status,
    userId: user.uid,
    userName: user.displayName,
    willUploadPicturesLater,
    followedBudgetRecommendation,
    followedChannelRecommendation,
    budgetRecommendation,
    locale,
    jobId,
    poNumber
  };

  if (campaignGoal == "applicants") {
    campaignData.activeSourcingFeature = true;
  }

  if (couponId) {
    campaignData.couponId = couponId;
  }

  // CASE 1: Campaign is being booked via selfService and the customer is not new
  if (selfServiceBookingStyle === "WITH_AUTO_LIVE") {
    withAdPreview = false;
    campaignData.isBookedViaSelfservice = true;
    campaignData.status = "SCHEDULED";
    campaignData.startDate = moment().add(DEFAULT_MINUTES_LEFT_TO_START, "minutes").format();
  }

  // CASE 2: Campaign is being booked via selfService and the customer is new
  if (selfServiceBookingStyle === "WITHOUT_AUTO_LIVE") {
    withAdPreview = false;
    campaignData.isBookedViaSelfservice = true;
    campaignData.status = "IN_PROGRESS";
  }

  return { withAdPreview, campaignData };
};
